import { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Modal } from 'components/Modal/Modal';
import { SLIPPAGE_GRADES } from 'consts';
import { store } from 'store';
import { INITIAL_SETTINGS } from 'store/swap/consts';

import * as S from './SettingsModal.styles';

const slippageVariants = [0.1, 0.5, 1];

export const SettingsModal = observer(() => {
  const { isSettingsOpen, closeSettings, settings, setSettings, validateSettings, fetchWowmax } =
    store.swapStore;
  const [slippageValue, setSlippageValue] = useState(settings.slippage.toString());

  useEffect(() => {
    setSlippageValue(settings.slippage.toString());
  }, [settings.slippage]);

  const handleSlippageInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    const positiveValue = value.replace('-', '');
    setSlippageValue(positiveValue);
  };

  const setValidSlippage = () => {
    const validSettings = validateSettings({
      slippage: slippageValue === '' ? INITIAL_SETTINGS.slippage : slippageValue,
    });
    setSettings(validSettings);
    setSlippageValue(String(validSettings.slippage));
  };

  const handleUsePMMChange = () => {
    setSettings({ usePMM: !settings.usePMM });
    fetchWowmax(true);
  };

  const onClose = () => {
    setValidSlippage();
    closeSettings();
  };

  let slippageWarning: string | ReactNode = '';
  if (slippageValue !== '') {
    if (+slippageValue > SLIPPAGE_GRADES.max) {
      slippageWarning = `The maximum slippage available is ${SLIPPAGE_GRADES.max}%`;
    } else if (+slippageValue > SLIPPAGE_GRADES.tooHight) {
      slippageWarning = 'Your transaction may be frontrun and result in an unfavorable trade';
    } else if (+slippageValue < SLIPPAGE_GRADES.tooSmall) {
      slippageWarning = 'Your transaction may fail';
    }
  }

  return (
    <Modal isOpen={isSettingsOpen} onClose={onClose} maxWidth={400} title="Settings">
      <S.ModalContent>
        <S.Title>Settings</S.Title>
        <S.Divider />
        <S.Blocks>
          <S.Block>
            <S.BlockTitle>Slippage Tolerance, %:</S.BlockTitle>
            <S.SlippageControls>
              {slippageVariants.map((value) => (
                <S.SlippageLabel key={value}>
                  <S.SlippageRadio
                    type="radio"
                    checked={value === +slippageValue}
                    onChange={() => setSettings({ slippage: value })}
                  />
                  <S.SlippageValue>{value.toFixed(1)}%</S.SlippageValue>
                </S.SlippageLabel>
              ))}
              <S.SlippageInput
                type="number"
                value={slippageValue}
                $isWarning={!!slippageWarning}
                onChange={handleSlippageInputChange}
                onBlur={setValidSlippage}
              />
              <S.SlippagePercent>%</S.SlippagePercent>
            </S.SlippageControls>
            {slippageWarning && <S.SlippageWarning>⚠ {slippageWarning}</S.SlippageWarning>}
          </S.Block>
          <S.Block>
            <S.UsePMM>
              <S.UsePMMIcon>⚡</S.UsePMMIcon>
              <S.UsePMMTexts>
                <S.UsePMMTitle>Boost rates with private liquidity</S.UsePMMTitle>
                <S.UsePMMText>
                  Requires quick transaction confirmation: a delay will cause transaction failure
                  and partial gas fee loss.
                </S.UsePMMText>
              </S.UsePMMTexts>
              <S.UsePMMLabel>
                <S.UsePMMInput
                  type="checkbox"
                  checked={settings.usePMM}
                  onChange={handleUsePMMChange}
                />
                <S.UsePMMButton>
                  <S.UsePMMButtonRound />
                </S.UsePMMButton>
              </S.UsePMMLabel>
            </S.UsePMM>
          </S.Block>
        </S.Blocks>
      </S.ModalContent>
    </Modal>
  );
});
